import React, { useState } from 'react';
import { useShop } from '../contexts/ShopContext';
import { Shop, MembershipRole } from '../types';

// Helper function to format shop type for display
const formatShopType = (type: string): string => {
  switch (type) {
    case 'PERSONAL':
      return 'Personal Shop';
    case 'BUSINESS':
      return 'Business Shop';
    case 'BUSINESS_PLUS':
      return 'Business+ Shop';
    default:
      return type;
  }
};

// Shop type pill — ONE style for every tier.
//
// This used to return three different shades ("cool gray-blue", "light blue", "medium blue") as an
// escalating ramp, which only read as a ramp while --primary was itself a blue. It is now gold, so
// BUSINESS_PLUS came out as a gold pill wearing light-blue text at roughly 1.3:1. The tier is a
// label, not a ranking the user acts on, so it gets the neutral chip treatment.
const getShopTypePillColor = (_type: string): { bg: string; text: string } => {
  return { bg: 'var(--bg-accent)', text: 'var(--text-accent)' };
};

// Helper function to get role pill color
const getRolePillColor = (role: MembershipRole): { bg: string; text: string } => {
  switch (role) {
    // Gold marks the one elevated role, but the label has to be the near-black inverse: light-blue
    // on gold was ~1.3:1 once --primary stopped being a blue.
    case 'PLATFORM_ADMIN':
      return { bg: 'var(--primary)', text: 'var(--text-inverse)' };
    case 'OWNER':
      return { bg: 'var(--bg-accent)', text: 'var(--text-accent)' }; // Medium blue
    case 'ADMIN':
      return { bg: 'var(--bg-accent)', text: 'var(--text-accent)' }; // Light blue
    case 'MEMBER':
      return { bg: 'var(--bg-secondary)', text: 'var(--text-accent)' }; // Cool gray-blue
    case 'VIEWER':
      return { bg: 'var(--bg-accent)', text: 'var(--text-accent)' }; // Light cool gray
    // --bg-primary is the page ground, so these pills were invisible against it.
    case 'CLIENT':
      return { bg: 'var(--bg-secondary)', text: 'var(--text-accent)' };
    case 'VENDOR':
      return { bg: 'var(--bg-secondary)', text: 'var(--text-accent)' };
    default:
      return { bg: 'var(--bg-accent)', text: 'var(--text-accent)' }; // Neutral cool gray
  }
};

const ShopSelector: React.FC = () => {
  const { currentShop, userShops, userMemberships, switchShop, loading } = useShop();
  const [isOpen, setIsOpen] = useState(false);

  // Helper to get membership roles for a shop
  const getMembershipRolesForShop = (shopId: string): MembershipRole[] => {
    const membership = userMemberships.find(m => m.shopId === shopId && m.active);
    return membership?.roles || [];
  };

  if (loading) {
    return (
      <div className="shop-selector-loading">
        <div className="spinner-sm"></div>
        <span className="text-muted">Loading shops...</span>
      </div>
    );
  }

  // Handle case when no shop is selected but shops are available
  if (!currentShop && userShops.length > 0) {
    return (
      <div className="shop-selector">
        <label className="form-label shop-selector-label">Select Shop</label>
        <div className="relative">
          <button
            className="shop-selector-button shop-selector-placeholder"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          >
            <div className="shop-info">
              <div className="shop-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 12l2 2 4-4"/>
                </svg>
              </div>
              <div className="shop-details">
                <span className="shop-name">Select a shop</span>
                <span className="shop-type">{userShops.length} available</span>
              </div>
            </div>
            <svg className="chevron-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </button>

          {isOpen && (
            <div className="shop-dropdown">
              <div className="shop-dropdown-header">
                <span className="text-sm text-muted">Choose Shop ({userShops.length})</span>
              </div>
              <ul className="shop-list" role="listbox">
                {userShops.map((shop) => {
                  const shopTypePillColor = getShopTypePillColor(shop.type);
                  const membershipRoles = getMembershipRolesForShop(shop.id);

                  return (
                    <li key={shop.id} className="shop-option" role="option" aria-selected="false">
                      <button
                        className="shop-option-button"
                        onClick={() => handleShopSelect(shop)}
                        style={{ padding: '8px 12px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                          <div className="shop-icon" style={{ flexShrink: 0 }}>
                            {shop.type === 'BUSINESS' || shop.type === 'BUSINESS_PLUS' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 21h18"/>
                                <path d="M5 21V7l8-4v18"/>
                                <path d="M19 21V11l-6-4"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                            )}
                          </div>
                          <span className="shop-name" style={{ fontWeight: 500, flexShrink: 0 }}>{shop.name}</span>
                          <span
                            style={{
                              padding: '2px 8px',
                              backgroundColor: shopTypePillColor.bg,
                              color: shopTypePillColor.text,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {formatShopType(shop.type)}
                          </span>
                          {membershipRoles.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                              {membershipRoles.map(role => {
                                const rolePillColor = getRolePillColor(role);
                                return (
                                  <span
                                    key={role}
                                    style={{
                                      padding: '2px 6px',
                                      backgroundColor: rolePillColor.bg,
                                      color: rolePillColor.text,
                                      borderRadius: '10px',
                                      fontSize: '0.65rem',
                                      fontWeight: 500,
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {role}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleShopSelect = async (shop: Shop) => {
    setIsOpen(false);
    if (!currentShop || shop.id !== currentShop.id) {
      await switchShop(shop.id);
    }
  };

  // Only render this section if currentShop exists
  if (!currentShop) {
    return (
      <div className="shop-selector">
        <div className="shop-selector-empty">
          <span className="text-sm text-muted">No shop selected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-selector">
      <div className="relative">
        <button
          className="shop-selector-button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="shop-info">
            <div className="shop-icon">
              {currentShop.type === 'BUSINESS' || currentShop.type === 'BUSINESS_PLUS' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18"/>
                  <path d="M5 21V7l8-4v18"/>
                  <path d="M19 21V11l-6-4"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              )}
            </div>
            <div className="shop-details">
              <span className="shop-name">{currentShop.name}</span>
            </div>
          </div>
          <svg className="chevron-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </button>

        {isOpen && (
          <div className="shop-dropdown">
            <div className="shop-dropdown-header">
              <span className="text-sm text-muted">Switch Shop ({userShops.length})</span>
            </div>
            <ul className="shop-list" role="listbox">
              {userShops.map((shop) => {
                const shopTypePillColor = getShopTypePillColor(shop.type);
                const membershipRoles = getMembershipRolesForShop(shop.id);
                const isCurrentShop = shop.id === currentShop?.id;

                return (
                  <li key={shop.id} className="shop-option" role="option" aria-selected={isCurrentShop}>
                    <button
                      className={`shop-option-button ${isCurrentShop ? 'active' : ''}`}
                      onClick={() => handleShopSelect(shop)}
                      style={{ padding: '8px 12px' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <div className="shop-icon" style={{ flexShrink: 0 }}>
                          {shop.type === 'BUSINESS' || shop.type === 'BUSINESS_PLUS' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 21h18"/>
                              <path d="M5 21V7l8-4v18"/>
                              <path d="M19 21V11l-6-4"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                              <circle cx="12" cy="7" r="4"/>
                            </svg>
                          )}
                        </div>
                        <span className="shop-name" style={{ fontWeight: 500, flexShrink: 0 }}>{shop.name}</span>
                        <span
                          style={{
                            padding: '2px 8px',
                            backgroundColor: shopTypePillColor.bg,
                            color: shopTypePillColor.text,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          {formatShopType(shop.type)}
                        </span>
                        {membershipRoles.length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                            {membershipRoles.map(role => {
                              const rolePillColor = getRolePillColor(role);
                              return (
                                <span
                                  key={role}
                                  style={{
                                    padding: '2px 6px',
                                    backgroundColor: rolePillColor.bg,
                                    color: rolePillColor.text,
                                    borderRadius: '10px',
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {role}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {isCurrentShop && (
                          <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '8px', flexShrink: 0 }}>
                            <polyline points="20,6 9,17 4,12"></polyline>
                          </svg>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopSelector;